import React, { FC, useCallback } from 'react';

import Heading from '@atlaskit/heading';
import { SkeletonItem } from '@atlaskit/menu';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import SectionMessage, {
  SectionMessageAction,
} from '@atlaskit/section-message';

import { useIntl } from 'src/hooks/intl';
import { EnabledCustomOnMergeChecks } from 'src/types/custom-merge-checks';

import { messages } from './forge-enabled-on-merge-checks.i18n';
import { CustomMergeCheckItem } from './merge-check-item';
import { mergeCheckItemSkeletonOverrideCss } from './styled';
import { makeCustomCheckKey } from './utils';

/* eslint @typescript-eslint/ban-types: "warn" */
export const ForgeEnabledOnMergeChecks: FC<EnabledCustomOnMergeChecks> = ({
  enabledChecks,
  error,
  refresh,
}) => {
  const { formatMessage } = useIntl();

  const Header = (
    <Box xcss={xcss({ marginBottom: 'space.050' })}>
      <Heading testId="forgeEnabledOnMergeChecksHeading" variant="xsmall">
        {formatMessage(messages.heading, {
          total: enabledChecks?.length ?? 0,
        })}
      </Heading>
    </Box>
  );

  const renderBody = useCallback(() => {
    if (error) {
      return (
        <>
          <SectionMessage
            appearance="warning"
            actions={
              <SectionMessageAction onClick={refresh}>
                {formatMessage(messages.retry)}
              </SectionMessageAction>
            }
            title={formatMessage(messages.errorTitle)}
          >
            {formatMessage(messages.errorText)}
          </SectionMessage>
        </>
      );
    }

    if (!enabledChecks) {
      return (
        <>
          {[...Array(3).keys()].map(index => (
            <SkeletonItem
              key={index}
              cssFn={mergeCheckItemSkeletonOverrideCss}
              hasAvatar
              isShimmering
              testId="forgeOnMergeChecksLoadingItem"
              width="100%"
            />
          ))}
        </>
      );
    }

    return (
      <Flex direction="column" gap="space.250">
        <div>
          {formatMessage(messages.description, {
            total: enabledChecks.length,
          })}
        </div>
        <Flex direction="column" gap="space.050">
          {enabledChecks.map(check => {
            const key = makeCustomCheckKey(check);
            const trailing =
              check.branchConfig.identifier !== 'all_branches'
                ? formatMessage(messages.checkTargetBranchLabel, {
                    branchName: check.branchConfig.identifier,
                  })
                : null;

            return (
              <CustomMergeCheckItem
                key={key}
                mergeCheckKey={key}
                result={check}
                trailing={trailing}
              />
            );
          })}
        </Flex>
      </Flex>
    );
  }, [enabledChecks, error, formatMessage, refresh]);

  if (enabledChecks?.length === 0) {
    return null;
  }

  return (
    // This component is used in the pull request merge dialog, the padding is added specifically
    // for that use case
    <Box xcss={xcss({ paddingBottom: 'space.200' })}>
      <Flex direction="column" gap="space.050">
        {Header}
        {renderBody()}
      </Flex>
    </Box>
  );
};
